import { useEffect, useRef, useState, useContext } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { lighten } from "@mui/material/styles";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import request from "../../../utilities/requests";
import { EventContext } from "../../../contexts/EventContext";
dayjs.extend(utc);
const GRANULARITIES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const formatPeriod = (iso, granularity) => {
  const d = dayjs.utc(iso);
  if (!d.isValid()) return "";
  if (granularity === "monthly") return d.format("MMM YYYY");
  return d.format("MMM D");
};

const formatCurrency = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);

export default function SalesChart({ data }) {
  const { userData } = useContext(EventContext);
  const primaryColor = userData?.org?.style?.primaryColor || "#1976d2";
  const secondaryColor = lighten(primaryColor, 0.45);

  const [rows, setRows] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [granularity, setGranularity] = useState("weekly");
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const skipNextFetch = useRef(false);

  useEffect(() => {
    if (data && !initialLoaded) {
      setRows(data.data || []);
      setStartDate(dayjs(data.start).format("YYYY-MM-DD"));
      setEndDate(dayjs(data.end).format("YYYY-MM-DD"));
      setGranularity(data.granularity || "weekly");
      skipNextFetch.current = true;
      setInitialLoaded(true);
    }
  }, [data, initialLoaded]);

  const handleGranularityChange = (newGranularity) => {
    setGranularity(newGranularity);
    if (!startDate || !endDate) return;
    if (newGranularity === "monthly") {
      const start = dayjs(startDate).startOf("month");
      const endDay = dayjs(endDate);
      const isLastDay = endDay.isSame(endDay.endOf("month"), "day");
      const end = isLastDay
        ? endDay
        : endDay.subtract(1, "month").endOf("month");
      setStartDate(start.format("YYYY-MM-DD"));
      setEndDate(end.format("YYYY-MM-DD"));
    } else if (newGranularity === "daily") {
      const start = dayjs(endDate).subtract(30, "day");
      setStartDate(start.format("YYYY-MM-DD"));
    }
  };

  const runReport = async () => {
    setLoading(true);
    const url = `reports/?reportName=sales&startDate=${startDate}&endDate=${endDate}&granularity=${granularity}`;
    const result = await request(url);
    if (result.success) {
      setRows(result.data.data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!initialLoaded) return;
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    if (!startDate || !endDate) return;
    runReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, granularity, initialLoaded]);

  const chartData = rows.map((r) => ({
    ...r,
    label: formatPeriod(r.periodStart, granularity),
  }));

  return (
    <Box sx={{ width: "100%", padding: 2 }}>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="granularity-label">Granularity</InputLabel>
          <Select
            labelId="granularity-label"
            label="Granularity"
            value={granularity}
            onChange={(e) => handleGranularityChange(e.target.value)}
          >
            {GRANULARITIES.map((g) => (
              <MenuItem key={g.value} value={g.value}>
                {g.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {loading ? <CircularProgress size={24} /> : null}
      </Box>

      {chartData.length === 0 ? (
        <Box
          sx={{
            height: 400,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography>No sales data for the selected range</Typography>
        </Box>
      ) : (
        <Box sx={{ width: "100%", height: 480 }}>
          <ResponsiveContainer>
            <ComposedChart
              data={chartData}
              margin={{ top: 20, right: 30, bottom: 20, left: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="label" />
              <YAxis
                yAxisId="left"
                tickFormatter={(v) => formatCurrency(v)}
                width={80}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                allowDecimals={false}
              />
              <Tooltip
                formatter={(value, name) => {
                  if (name === "Orders") return [value, name];
                  return [formatCurrency(value), name];
                }}
              />
              <Legend />
              <Bar
                yAxisId="left"
                dataKey="tnfmSales"
                name="TNFM Sales"
                stackId="sales"
                fill={primaryColor}
              />
              <Bar
                yAxisId="left"
                dataKey="eventbriteSales"
                name="Eventbrite Sales"
                stackId="sales"
                fill={secondaryColor}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="totalOrders"
                name="Orders"
                stroke="#444"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
}
